import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./Scheduled.module.sass";
import Card from "../../../components/Card";
import Form from "../../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../../components/Icon";
import ModalTutor from "../../../components/ModalTutor";

// Services
import { getTutorsByCompany, createTutor, updateTutor, deleteTutor } from "../../../services/tutor.service";
import { updateCompany } from "../../../services/company.service";
import { showSuccess, showError } from "../../../services/toast.service";

const Tutor = ({ company }) => {
    const [search, setSearch] = useState("");
    const [tutors, setTutors] = useState([]);
    const [filteredTutors, setFilteredTutors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTutor, setEditingTutor] = useState(null);
    const [selectedTutors, setSelectedTutors] = useState([]);

    const fetchTutors = async () => {
        if (!company || !company.id) return;
        try {
            setLoading(true);
            const tutorsData = await getTutorsByCompany(company.id);
            setTutors(tutorsData);
            setFilteredTutors(tutorsData);
        } catch (error) {
            console.error('Erreur lors de la récupération des tuteurs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (company) {
            fetchTutors();
        }
    }, [company]);

    const handleOpenModal = (tutor = null) => {
        setEditingTutor(tutor);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditingTutor(null);
        setIsModalOpen(false);
    };

    const handleSaveTutor = async (formData) => {
        if (!company || !company.id) {
            showError("Impossible de sauvegarder le tuteur sans référence à l'entreprise.");
            return;
        }
        try {
            if (editingTutor) {
                const updatedTutor = await updateTutor(editingTutor.id, formData);
                setTutors(tutors.map(t => t.id === editingTutor.id ? updatedTutor : t));
                setFilteredTutors(filteredTutors.map(t => t.id === editingTutor.id ? updatedTutor : t));
                showSuccess('Tuteur mis à jour avec succès.');
            } else {
                const dataToCreate = { ...formData, company_id: company.id };
                const newTutor = await createTutor(dataToCreate);

                const updatedTutorIds = [...(company.tutor_id || []), newTutor.id];
                await updateCompany(company.id, { tutor_id: updatedTutorIds });

                setTutors([...tutors, newTutor]);
                setFilteredTutors([...filteredTutors, newTutor]);
                showSuccess('Tuteur créé avec succès.');
            }
            handleCloseModal();
            fetchTutors(); // Refresh list
        } catch (error) {
            showError('Erreur lors de la sauvegarde du tuteur.');
            console.error(error);
        }
    };

    const handleSelectTutor = (tutorId, isSelected) => {
        if (isSelected) {
            setSelectedTutors([...selectedTutors, tutorId]);
        } else {
            setSelectedTutors(selectedTutors.filter(id => id !== tutorId));
        }
    };

    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            const allTutorIds = filteredTutors.map(tutor => tutor.id);
            setSelectedTutors(allTutorIds);
        } else {
            setSelectedTutors([]);
        }
    };

    const handleClearSelection = () => {
        setSelectedTutors([]);
    };

    const handleDeleteSelected = async (selectedIds) => {
        if (!company || !company.id || !selectedIds.length) {
            return;
        }

        // Filtrer les IDs des tuteurs de l'entreprise
        const updatedTutorIds = (company.tutor_id || []).filter(id => !selectedIds.includes(id));
        
        try {
            // Mettre à jour l'entreprise
            await updateCompany(company.id, { tutor_id: updatedTutorIds });
            
            // Supprimer chaque tuteur
            for (const tutorId of selectedIds) {
                await deleteTutor(tutorId);
            }
            
            // Mettre à jour l'état local
            const remainingTutors = tutors.filter(t => !selectedIds.includes(t.id));
            setTutors(remainingTutors);
            setFilteredTutors(remainingTutors.filter(tutor => {
                if (!search.trim()) return true;
                const searchLower = search.toLowerCase();
                const fullName = `${tutor.first_name} ${tutor.last_name}`.toLowerCase();
                const email = (tutor.email || '').toLowerCase();
                return fullName.includes(searchLower) || email.includes(searchLower);
            }));
            
            return true;
        } catch (error) {
            console.error('Erreur lors de la suppression des tuteurs:', error);
            throw error;
        }
    };

    const handleDeleteTutor = async (tutorId) => {
        if (!company || !company.id) {
            showError("Impossible de supprimer le tuteur sans référence à l'entreprise.");
            return;
        }
        try {
            const updatedTutorIds = (company.tutor_id || []).filter(id => id !== tutorId);
            await updateCompany(company.id, { tutor_id: updatedTutorIds });

            await deleteTutor(tutorId);

            setTutors(tutors.filter(t => t.id !== tutorId));
            setFilteredTutors(filteredTutors.filter(t => t.id !== tutorId));
            showSuccess('Tuteur supprimé avec succès.');
        } catch (error) {
            showError('Erreur lors de la suppression du tuteur.');
            console.error(error);
        }
    };

    // Fonction pour filtrer les tuteurs en fonction du texte de recherche
    const filterTutors = (searchText) => {
        if (!searchText.trim()) {
            setFilteredTutors(tutors);
            return;
        }

        const searchLower = searchText.toLowerCase();
        const filtered = tutors.filter(tutor => {
            const fullName = `${tutor.first_name} ${tutor.last_name}`.toLowerCase();
            const email = (tutor.email || '').toLowerCase();
            const jobTitle = (tutor.job_title || '').toLowerCase();
            const phone = (tutor.phone || '').toLowerCase();

            return fullName.includes(searchLower) ||
                email.includes(searchLower) ||
                jobTitle.includes(searchLower) ||
                phone.includes(searchLower);
        });

        setFilteredTutors(filtered);
    };

    // Effet pour filtrer les tuteurs lorsque la recherche change
    useEffect(() => {
        filterTutors(search);
    }, [search, tutors]);

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        filterTutors(search);
    };

    return (
        <>
            <Card
                className={styles.card}
                classCardHead={styles.head}
                title="Maîtres d'apprentissage"
                classTitle={cn("title-blue", styles.title)}
                head={
                    <>
                      <Form
                        className={styles.form}
                        value={search}
                        setValue={setSearch}
                        onSubmit={() => handleSubmit()}
                        placeholder="Rechercher par nom, prénom, email"
                        type="text"
                        name="search"
                        icon="search"
                      />
                      <div className={styles.sorting}>
                        <button className={cn("button-stroke", styles.button)} onClick={() => handleOpenModal()}>
                            <Icon name="plus" size="16" />
                            <span>Ajouter un tuteur</span>
                        </button>
                      </div>
                    </>
                  }
                >
                <div className={styles.wrapper}>
                    {loading ? (
                        <div className={styles.loading}>
                            <div className="spinner-large" />
                        </div>
                    ) : filteredTutors.length > 0 ? (
                        <Table 
                            items={filteredTutors} 
                            title="Tuteurs de l'entreprise" 
                            onEdit={handleOpenModal}
                            onDelete={handleDeleteTutor}
                            selectedTutors={selectedTutors}
                            onSelectTutor={handleSelectTutor}
                            onSelectAll={handleSelectAll}
                        />
                    ) : tutors.length > 0 ? (
                        <div className={styles.empty}>Aucun tuteur ne correspond à votre recherche</div>
                    ) : (
                        <div className={styles.empty}>Aucun tuteur trouvé pour cette entreprise.</div>
                    )}
                </div>

                {isModalOpen && (
                    <ModalTutor
                        visible={isModalOpen}
                        onClose={handleCloseModal}
                        onSave={handleSaveTutor}
                        tutor={editingTutor}
                    />
                )}
            </Card>
            <Panel 
                selectedTutors={selectedTutors} 
                onDeleteSelected={handleDeleteSelected} 
                onClearSelection={handleClearSelection} 
            />
        </>
    );
};

export default Tutor;
